// /admin/claims — staff queue for group-claim + appeal decisions.
// Every action writes to the audit log via the API layer.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';

interface Claim {
    id: string;
    group_id: string;
    group_slug: string;
    group_name: string;
    requested_by_user_id: string;
    requester_display: string;
    kind: 'claim' | 'appeal';
    reason: string;
    evidence_url?: string | null;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
}

export function AdminClaims() {
    const api = useApi();
    const [claims, setClaims] = useState<Claim[]>([]);
    const [status, setStatus] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ claims: Claim[] }>('/api/admin/claims');
            setClaims(r.claims ?? []);
        } catch (e) { setStatus('Load failed: ' + (e as Error).message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function decide(c: Claim, approve: boolean) {
        const note = window.prompt(approve
            ? `Approve ${c.kind} on ${c.group_name}?\n\nOptional note (visible to requester):`
            : `Reject ${c.kind} on ${c.group_name}?\n\nReason for rejection:`) ?? '';
        if (!approve && note.trim() === '') { setStatus('Rejection needs a note.'); return; }
        try {
            await api.post('/api/admin/claims/' + c.id + (approve ? '/approve' : '/reject'), { note });
            await load();
        } catch (e) { setStatus('Failed: ' + (e as Error).message); }
    }

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1000, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontFamily: 'Newsreader,serif', fontSize: 28 }}>Group claims</h1>
            </div>
            <nav style={adminNav}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>

            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                {claims.length} pending. Approve = grant admin to requester + flip group to claimed.
                Reject = mark rejected + include your reason.
            </p>

            {status && <p style={{ color: '#8b1919', fontSize: 12 }}>{status}</p>}

            {claims.length === 0 ? (
                <p style={{ color: '#999', fontStyle: 'italic', fontFamily: "'Newsreader',serif" }}>
                    Nothing to review right now.
                </p>
            ) : claims.map(c => (
                <article key={c.id} style={cardStyle}>
                    <header style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 8 }}>
                        <span style={{ ...kindPill, background: c.kind === 'appeal' ? '#f2dfc3' : '#c6e6c9', color: c.kind === 'appeal' ? '#8b1919' : '#2b5e3f' }}>
                            {c.kind}
                        </span>
                        <Link to={'/groups/' + c.group_slug + '/dashboard'} style={{ fontWeight: 700, fontSize: 16 }}>
                            {c.group_name}
                        </Link>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666' }}>
                            {new Date(c.created_at).toLocaleString()}
                        </span>
                    </header>

                    <div style={{ fontSize: 13, color: '#333', marginBottom: 6 }}>
                        Requested by{' '}
                        <Link to={'/user/' + c.requested_by_user_id}>{c.requester_display}</Link>
                    </div>
                    <div style={{ padding: 8, background: '#faf8f2', borderLeft: '3px solid var(--ink)', fontStyle: 'italic', fontFamily: "'Newsreader',serif" }}>
                        {c.reason}
                    </div>
                    {c.evidence_url && (
                        <div style={{ fontSize: 11, marginTop: 6 }}>
                            Evidence: <a href={c.evidence_url} target="_blank" rel="noopener noreferrer">{c.evidence_url}</a>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button type="button" onClick={() => decide(c, true)}
                                style={{ ...actionBtn, background: 'var(--accent)', color: '#fff' }}>
                            Approve
                        </button>
                        <button type="button" onClick={() => decide(c, false)}
                                style={{ ...actionBtn, background: 'none', color: '#8b1919', border: '1px solid #8b1919' }}>
                            Reject
                        </button>
                    </div>
                </article>
            ))}
        </main>
    );
}

const adminNav: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7', fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLink: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};
const cardStyle: React.CSSProperties = {
    padding: 14, marginBottom: 12,
    border: '1px solid #cfc9b7', background: '#fff',
};
const kindPill: React.CSSProperties = {
    display: 'inline-block', padding: '2px 8px', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: 2, fontFamily: '-apple-system, sans-serif', fontWeight: 700,
};
const actionBtn: React.CSSProperties = {
    padding: '6px 14px', border: 0, cursor: 'pointer',
    fontFamily: '-apple-system, sans-serif', fontSize: 12,
    textTransform: 'uppercase', letterSpacing: 1,
};
